"use client"

import { useState } from "react"
import Link from "next/link"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Progress } from "@/components/ui/progress"
import {
  BookOpen,
  ChevronRight,
  ChevronLeft,
  Sparkles,
  Heart,
  Lightbulb,
  TrendingUp,
  Users,
  DollarSign,
  Camera,
  CheckCircle2,
} from "lucide-react"
import { useReviewPersistence, type YearlyReviewResponses } from "../hooks/use-review-persistence"
import { SaveIndicator } from "./save-indicator"
import { getDefaultReviewYear } from "../utils"

interface ReviewFormProps {
  email: string
  userId?: string | null
  onComplete: (data: YearlyReviewResponses) => void
}

const sections = [
  {
    id: "frame",
    title: "Summary of the year",
    subtitle: "How would you describe the last year?",
    icon: Sparkles,
    color: "text-primary",
    scienceNote: {
      title: "Why this works",
      text: "Putting complex experiences into simple words helps us process the year as a whole.",
      source:
        "Narrative psychology research shows that creating coherent stories about our lives improves psychological well-being",
      link: "https://pmc.ncbi.nlm.nih.gov/articles/PMC4324396/",
    },
    questions: [
      {
        id: "year-was",
        label: "Last year was…",
        placeholder: "1-3 words or short phrases (e.g., transitional, healing, courageous, exciting, challenging, rewarding, etc.)",
        type: "short",
      },
    ],
  },
  {
    id: "me-last-year",
    title: "Remember the starting point",
    subtitle: "If you met a version of yourself from last year, how would you describe the difference between you now and you then?",
    icon: BookOpen,
    color: "text-secondary-foreground",
    scienceNote: {
      title: "Compare yourself only to yourself",
      text: "Comparing yourself to others does not help growth. It can inspire or show the possible ways you can choose, but always focus on yourself. This will make you happier.",
      source: "Upon comparing oneself to others",
      link: "https://www.researchgate.net/publication/370544470_Upon_Comparing_Oneself_to_Others",
    },
    questions: [
      {
        id: "health",
        label: "What changes I have made to my health and well-being",
        placeholder: "Did I sleep better, eat healthier, exercise more, or take better care of myself than I did a year ago? Did I start a new habit, stopped a bad one, or went to therapy?",
        type: "long",
      },
      {
        id: "free-time",
        label: "How did I spend my free time and rest",
        placeholder: "Was I taking care of my resources and my energy, did I have time to relax and recharge last year more than I did a year ago?",
        type: "long",
      },
      {
        id: "experience",
        label: "Work and experience in what I do",
        placeholder: "Professional changes, projects, learnings the new me can share",
        type: "long",
      },
      {
        id: "mental-setup",
        label: "How I changed my mental setup",
        placeholder: "Do I feel more confident and comfortable in my skin than the year before?",
        type: "long",
      },
      {
        id: "message-to-self",
        label: "What would I like to tell myself from a year ago?",
        placeholder: "Words of support, encouragement, advice. Something that would have helped me.",
        type: "long",
      },
    ],
  },
  {
    id: "wins",
    title: "Claiming Your Wins",
    subtitle: "Mean no less than achievements",
    icon: TrendingUp,
    color: "text-secondary-foreground",
    scienceNote: {
      title: "The neuroscience of acknowledging wins",
      text: "Recognizing even small victories releases dopamine and strengthens neural pathways associated with motivation and resilience. Acknowledging 'quiet wins' counters negativity bias—our brain's tendency to focus on what went wrong.",
      source: "Research on positive psychology and self-efficacy",
      link: "https://www.ncbi.nlm.nih.gov/pmc/articles/PMC6124958/",
    },
    questions: [
      {
        id: "quiet-wins",
        label: "My quiet or unseen wins",
        placeholder: "What did I do even though it was difficult and I was scared, or didn't want to do it?",
        type: "long",
      },
      {
        id: "let-go",
        label: "What I chose to let go of",
        placeholder: "And how it helped me to take better care of myself",
        type: "long",
      },
      {
        id: "chose-myself",
        label: "When I learned to say no and prioritize my needs",
        placeholder: "Moments when I put myself on the first place",
        type: "long",
      },
      {
        id: "progress",
        label: "Where I made progress, even if the goal wasn't fully achieved",
        placeholder: "It mustn't be a big thing, tiny changes matter. In my mindset, habits, stress response, boundaries...",
        type: "long",
      },
    ],
  },
  // {
  //   id: "journey",
  //   title: "The Year as a Journey",
  //   subtitle: "Not a checklist",
  //   icon: Lightbulb,
  //   color: "text-accent-foreground",
  //   scienceNote: {
  //     title: "Memory reconsolidation",
  //     text: "When you revisit significant memories and extract meaning from them, your brain literally rewrites those memories—a process called reconsolidation. This can reduce emotional charge and help you integrate difficult experiences.",
  //     source: "Neuroscience research on memory and learning",
  //     link: "https://link.springer.com/article/10.1007/s10615-020-00754-z",
  //   },
  //   //https://www.routledge.com/Unlocking-the-Emotional-Brain-Memory-Reconsolidation-and-the-Psychotherapy-of-Transformational-Change/Ecker-Ticic-Hulley/p/book/9781032139128
  //   questions: [
  //     {
  //       id: "turning-points",
  //       label: "Moments that changed me",
  //       placeholder: "An event, a conversation, a decision...",
  //       type: "long",
  //     },
  //     {
  //       id: "what-changed",
  //       label: "What I understood about myself after this",
  //       placeholder: "New insights and realizations",
  //       type: "long",
  //     },
  //     {
  //       id: "hardest-moments",
  //       label: "The hardest moments of the year — what did they teach me?",
  //       placeholder: "Without forcing gratitude — about my needs, boundaries, people, pace...",
  //       type: "long",
  //     },
  //   ],
  // },
  {
    id: "energy",
    title: "Energy & Well-Being",
    subtitle: "What nourished and what drained you",
    icon: Heart,
    color: "text-primary",
    scienceNote: {
      title: "Understanding your nervous system",
      text: "Tracking what drains vs. energizes you helps identify patterns in nervous system regulation. This awareness is the first step toward creating environments and routines that support your well-being.",
      source: "Polyvagal theory and somatic psychology research",
      link: "https://www.ncbi.nlm.nih.gov/pmc/articles/PMC3108032/",
    },
    questions: [
      {
        id: "body-felt",
        label: "How did my body feel for most of the year",
        placeholder: "Tense, stable, exhausted, recovering...",
        type: "short",
      },
      {
        id: "drained-energy",
        label: "What drained me",
        placeholder: "What took more than it gave",
        type: "long",
      },
      {
        id: "gave-energy",
        label: "What helped me feel better",
        placeholder: "People, activities, routines, information, care for my body",
        type: "long",
      },
      {
        id: "hobbies",
        label: "Hobbies and activities I did for fun which brought joy",
        placeholder: "Reading, writing, painting, coding, gardening, cooking, etc.",
        type: "long",
      },
      {
        id: "health-changes",
        label: "What would I like to bring to the next year for my health and well-being",
        placeholder: "Find favourite activities, do a check-up, get more sleep, cook healthier meals, return to old hobbies or find new...",
        type: "long",
      },
    ],
  },
  {
    id: "relationships",
    title: "Relationships & Environment",
    subtitle: "The people who walked with you",
    icon: Users,
    color: "text-secondary-foreground",
    scienceNote: {
      title: "Social connection and health",
      text: "Quality relationships are one of the strongest predictors of longevity and happiness. Reflecting on relationship patterns helps you identify which connections truly support your growth and well-being.",
      source: "Harvard Study of Adult Development (80+ years)",
      link: "https://www.health.harvard.edu/blog/the-secret-to-happiness-heres-some-advice-from-the-longest-running-study-on-happiness-2017100512543",
    },
    questions: [
      { 
        id: "who-supported", 
        label: "Who supported me", 
        placeholder: "The people who were truly there", 
        type: "long" 
      },
      { 
        id: "who-drifted", 
        label: "Who drifted away", 
        placeholder: "Natural changes in connection", 
        type: "long" 
      },
      {
        id: "partner",
        label: "How relationship with my partner changed",
        placeholder: "Became more open, honest, vulnerable, connected, distant, critical, supportive, challenging, loving, forgiving, understanding...",
        type: "long",
      },
      {
        id: "who-inspired",
        label: "Who was an inspiration last year",
        placeholder: "What did that person do that I want to do? Any person in the world would do, not only the ones I know personally.",
        type: "long",
      },
      {
        id: "community-changes",
        label: "What would I like to change about my community",
        placeholder: "Meet new people, spend more time with friends, connect with family, etc.",
        type: "long",
      },
    ],
  },
  // {
  //   id: "projects",
  //   title: "Projects & Studies",
  //   subtitle: "What I did for my personal growth and development",
  //   icon: Heart,
  //   color: "text-primary",
  //   scienceNote: {
  //     title: "See how you grow motivates you to go further. It's your ",
  //     text: "Tracking what drains vs. energizes you helps identify patterns in nervous system regulation. This awareness is the first step toward creating environments and routines that support your well-being.",
  //   },
  //   questions: [
  //     {
  //       id: "drained-energy",
  //       label: "What drained me",
  //       placeholder: "What took more than it gave",
  //       type: "long",
  //     },
  //     {
  //       id: "gave-energy",
  //       label: "What nourished me",
  //       placeholder: "People, activities, routines, information, care for my body",
  //       type: "long",
  //     },
  //     {
  //       id: "body-felt",
  //       label: "How did my body feel for most of the year?",
  //       placeholder: "Tense, stable, exhausted, recovering...",
  //       type: "short",
  //     },
  //     {
  //       id: "helped-feel-better",
  //       label: "What helped me feel better?",
  //       placeholder: "Activities, people, food, posture training, sleep...",
  //       type: "long",
  //     },
  //     {
  //       id: "health-changes",
  //       label: "What would I like to bring to the next year for my health",
  //       placeholder: "Find favourite activities, do a check-up, get more sleep, cook healthier meals...",
  //       type: "long",
  //     },
  //   ],
  // },
  {
    id: "finances",
    title: "Finances and assets",
    subtitle: "Without shame or moral judgment",
    icon: DollarSign,
    color: "text-accent-foreground",
    scienceNote: {
      title: "Financial awareness without shame",
      text: "Financial literacy helps reduce financial worries and improves financial stability, which in turn reduces depression, anxiety, and stress. Non-judgmental reflection on your finances helps you make clearer decisions and build better financial outcomes.",
      source: "Impact of financial literacy, mental budgeting and self control on financial wellbeing",
      link: "https://pmc.ncbi.nlm.nih.gov/articles/PMC10645357/",
    },
    questions: [
      {
        id: "income-trend",
        label: "My income last year",
        placeholder: "Increasing ↑ / Decreasing ↓ / Stable →",
        type: "short",
      },
      { id: "income-sources", 
        label: "Main sources of income", 
        placeholder: "List your income sources", 
        type: "long" },
      {
        id: "money-feelings",
        label: "How did I feel about money last year?",
        placeholder: "Calm, anxious, hopeful, controlling...",
        type: "short",
      },
      // {
      //   id: "projects",
      //   label: "Projects I worked on",
      //   placeholder: "What I've built and developed",
      //   type: "long",
      // },
      // {
      //   id: "reputation",
      //   label: "My assets (money, skills, projects, reputation)",
      //   placeholder: "What you've built and developed",
      //   type: "long",
      // },
      {
        id: "assets",
        label: "My assets (money, skills, projects, reputation)",
        placeholder: "Was I satisfied with my income, what I've built and developed, skills learned, started a blog or grew it...",
        type: "long",
      },
      {
        id: "money-changes",
        label: "What changes would I like to change about my assets next year",
        placeholder: "Saving more, learn to invest, spending less, learning marketing, speaking at conference...",
        type: "long",
      },
    ],
  },
  {
    id: "memories",
    title: "Memory & Life",
    subtitle: "The moments that matter",
    icon: Camera,
    color: "text-primary",
    scienceNote: {
      title: "Peak moments shape your narrative",
      text: "Research shows we remember peaks (best/worst moments) and endings more than averages. Identifying your peak positive moments strengthens those neural pathways and helps you recognize what brings authentic joy.",
      source: "Peak-end rule and mental health",
      link: "https://pmc.ncbi.nlm.nih.gov/articles/PMC11343653/",
    },
    questions: [
      {
        id: "start-year",
        label: "Best memories from beginning of the year",
        placeholder: "Nice moments with people, travel, projects, feeling of success...",
        type: "long",
      },
      {
        id: "spring",
        label: "Best memories from spring",
        placeholder: "Nice moments with people, travel, projects, feeling of success...",
        type: "long",
      },
      {
        id: "summer",
        label: "Best memories from summer",
        placeholder: "Nice moments with people, travel, projects, feeling of success...",
        type: "long",
      },
      {
        id: "autumn",
        label: "Best memories from autumn",
        placeholder: "Nice moments with people, travel, projects, feeling of success...",
        type: "long",
      },
      {
        id: "end-year",
        label: "Best memories from end of the year",
        placeholder: "Nice moments with people, travel, projects, feeling of success...",
        type: "long",
      },
      {
        id: "warmest-moment",
        label: "The happiest moment of the year",
        placeholder: "When did I feel most joyful and content? When I could feel it in my body?",
        type: "long",
      },
      {
        id: "most-alive",
        label: "The most alive moment",
        placeholder: "When did I feel fully present and engaged?",
        type: "long",
      },
      {
        id: "insight",
        label: "An insight of the year",
        placeholder: "One or several ideas that shifted my perspective",
        type: "long",
      }
    ],
  },
  {
    id: "closing",
    title: "Closing the Year",
    subtitle: "A gentle ritual",
    icon: CheckCircle2,
    color: "text-secondary-foreground",
    scienceNote: {
      title: "Rituals and psychological closure",
      text: "Closure rituals help your brain mark transitions and process endings. This practice of 'letting go' activates prefrontal regions associated with emotional regulation, making it easier to move forward without rumination.",
      source: "Research on ritual, closure, and emotional processing",
      link: "https://pmc.ncbi.nlm.nih.gov/articles/PMC12635995/",
    },
    questions: [
      {
        id: "letting-go",
        label: "What I am letting go of",
        placeholder: "Expectations, roles, promises, projects that are no longer relevant",
        type: "long",
      },
      {
        id: "grateful-to-myself",
        label: "What I am grateful to myself for last year",
        placeholder: "Not to others — specifically to myself",
        type: "long",
      },
      {
        id: "message-to-future-self",
        label: "A message to myself for the next year",
        placeholder: "I know it may be hard for you... I believe in you because...",
        type: "long",
      },
    ],
  },
]

export function ReviewForm({ email, userId, onComplete }: ReviewFormProps) {
  const currentYear = getDefaultReviewYear()
  // Start from section 0 (includes "The overall description")
  const [currentSection, setCurrentSection] = useState(0)
  
  const { responses, updateResponses, flushSave, saveStatus } = useReviewPersistence({
    email,
    reviewYear: currentYear,
  })

  // Progress calculation: include all sections
  const totalSectionsCount = sections.length
  const progress = ((currentSection + 1) / totalSectionsCount) * 100

  const handleNext = async () => {
    if (currentSection < sections.length - 1) {
      await flushSave()
      setCurrentSection(currentSection + 1)
      window.scrollTo({ top: 0, behavior: "smooth" })
    } else {
      // Complete review
      await flushSave()
      // Mark as completed
      await flushSave()
      onComplete(responses)
    }
  }

  const handlePrevious = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1)
      window.scrollTo({ top: 0, behavior: "smooth" })
    }
  }

  const updateAnswer = (questionId: string, value: string) => {
    updateResponses({
      [questionId]: value,
    } as Partial<YearlyReviewResponses>)
  }

  const section = sections[currentSection]
  const Icon = section?.icon

  return (
    <div className="space-y-6">
      <SaveIndicator status={saveStatus} />
      
      <div className="mb-8 space-y-4">
        <div className="flex items-center gap-3">
          {Icon && <Icon className={`h-8 w-8 ${section.color}`} />}
          <div className="flex-1">
            <h2 className="text-3xl md:text-4xl font-bold">{section.title}</h2>
            <p className="text-muted-foreground mt-1">{section.subtitle}</p>
          </div>
          {/* Subtle top navigation */}
          <div className="flex items-center gap-1 opacity-40 hover:opacity-100 transition-opacity">
            <Button
              variant="ghost"
              size="icon"
              onClick={handlePrevious}
              disabled={currentSection === 0}
              className="h-8 w-8 text-muted-foreground hover:text-foreground"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleNext}
              className="h-8 w-8 text-muted-foreground hover:text-foreground"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="space-y-2">
        <div className="flex justify-between text-sm text-muted-foreground">
          <span>Section {currentSection + 1} of {totalSectionsCount}</span>
          <span>{Math.round(progress)}% complete</span>
        </div>
        <Progress value={progress} />
      </div>

      {/* Science note card */}
      {section.scienceNote && (
        <Card className="mb-8 p-6 bg-muted/30 border-l-4 border-l-primary">
          <div className="flex items-start gap-3">
            <Lightbulb className="h-5 w-5 text-primary mt-1 shrink-0" />
            <div className="space-y-2">
              <h3 className="font-semibold text-sm uppercase tracking-wide text-primary">
                {section.scienceNote.title}
              </h3>
              <p className="text-sm leading-relaxed text-muted-foreground">{section.scienceNote.text}</p>
              <a
                href={section.scienceNote.link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
              >
                Read the research →
              </a>
            </div>
          </div>
        </Card>
      )}

      <div className="space-y-8">
        {section.questions.map((question) => (
          <div key={question.id} className="space-y-3">
            <label className="block text-lg font-medium leading-relaxed">{question.label}</label>
            {question.type === "short" ? (
              <input
                type="text"
                value={(responses[question.id as keyof YearlyReviewResponses] as string) || ""}
                onChange={(e) => updateAnswer(question.id, e.target.value)}
                onBlur={flushSave}
                placeholder={question.placeholder}
                className="w-full px-4 py-3 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 transition-shadow"
              />
            ) : (
              <textarea
                value={(responses[question.id as keyof YearlyReviewResponses] as string) || ""}
                onChange={(e) => updateAnswer(question.id, e.target.value)}
            onBlur={flushSave}
                placeholder={question.placeholder}
                rows={4}
                className="w-full px-4 py-3 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 transition-shadow resize-none"
              />
            )}
            <p className="text-sm text-muted-foreground italic">{question.placeholder}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mt-12 pt-8 border-t">
        <Button variant="ghost" onClick={handlePrevious} disabled={currentSection === 0}>
          Previous
            </Button>
        <div className="text-sm text-muted-foreground">
          {currentSection + 1} of {totalSectionsCount}
        </div>
            <Button onClick={handleNext}>
          {currentSection === sections.length - 1 ? "Complete Reflection" : "Continue"}
          <ChevronRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
    </div>
  )
}

